import React, { useContext, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  Building2,
  Building,
  Users,
  DollarSign,
  Cpu,
  Calendar,
  ShoppingBag,
  Info,
  Mail,
} from "lucide-react";
import TruncateTest from "../../common/TruncateTest";
import { AuthContext } from "../../context/AuthContext";
import AttractiveEmptyState from "../../common/AttractiveEmptyState";
import ViewEmailPhoneModal from "../../common/ViewEmailPhoneModal";

const AboutCompany = ({ checkLoginStatus }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const [showEmailPhoneModal, setShowEmailPhoneModal] = useState(false);
  const companyDetails = useSelector(
    (state) => state.companyDetailsReducer.companyDetails,
  );
  const lockedValue = "Locked. Sign in to view";
  const getProtectedValue = (value) =>
    isAuthenticated ? value || "-" : lockedValue;
  const updatedAt =
    companyDetails?.lastUpdated ||
    companyDetails?.lastModifiedDate ||
    companyDetails?.updatedDate ||
    companyDetails?.createdDate;

  const hasAnyData =
    companyDetails?.introduction ||
    companyDetails?.range?.name ||
    companyDetails?.revenue?.name ||
    companyDetails?.category?.name ||
    companyDetails?.industry?.name ||
    companyDetails?.technographics ||
    companyDetails?.productServices;

  const productServicesText = isAuthenticated
    ? companyDetails?.productServices
    : lockedValue;
  const lastUpdatedText = isAuthenticated
    ? updatedAt
      ? moment(updatedAt).format("D MMMM YYYY, h:mm a")
      : "-"
    : lockedValue;

  if (!hasAnyData) {
    return (
      <AttractiveEmptyState
        icon={Building2}
        title="No company details available"
        description="Company information will appear here once loaded."
      />
    );
  }

  return (
    <div className="tw-text-sm tw-mb-4 tw-p-6 tw-font-inter tw-flex tw-flex-col tw-bg-white">
      {/* Summary Box */}
      <div className="tw-bg-slate-50/30 tw-border tw-border-solid tw-border-slate-100 tw-rounded-xl tw-p-5 tw-mb-6">
        <div className="tw-flex tw-items-center tw-gap-2.5 tw-mb-2.5 tw-text-[13px] tw-font-bold tw-text-slate-800">
          <Info size={16} className="tw-text-slate-500" />
          <span>Summary</span>
        </div>
        <div className="tw-pl-7 tw-text-xs tw-font-medium tw-text-slate-600 tw-leading-relaxed">
          {companyDetails?.introduction ? (
            <TruncateTest
              description={companyDetails?.introduction}
              onAuth={() => (isAuthenticated ? checkLoginStatus?.() : setShowEmailPhoneModal(true))}
            />
          ) : (
            <div className="tw-text-slate-400">No summary available</div>
          )}
        </div>
      </div>

      {/* Metadata Grid */}
      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-x-8 tw-gap-y-6 tw-mb-6">
        {/* Employees Range */}
        <div className="tw-flex tw-flex-col">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Users size={16} className="tw-text-slate-500" />
            <span className="tw-text-xs tw-font-bold tw-text-slate-700">Employees Range</span>
          </div>
          <div className="tw-text-xs tw-font-medium tw-text-slate-500 tw-pl-7 tw-mt-1 blur-text">
            {getProtectedValue(companyDetails?.range?.name)}
          </div>
        </div>

        {/* Revenue Range */}
        <div className="tw-flex tw-flex-col">
          <div className="tw-flex tw-items-center tw-gap-2">
            <DollarSign size={16} className="tw-text-slate-500" />
            <span className="tw-text-xs tw-font-bold tw-text-slate-700">Revenue Range</span>
          </div>
          <div className="tw-text-xs tw-font-medium tw-text-slate-500 tw-pl-7 tw-mt-1 blur-text">
            {getProtectedValue(companyDetails?.revenue?.name)}
          </div>
        </div>

        {/* Company Type */}
        <div className="tw-flex tw-flex-col">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Building size={16} className="tw-text-slate-500" />
            <span className="tw-text-xs tw-font-bold tw-text-slate-700">Company Type</span>
          </div>
          <div className="tw-text-xs tw-font-medium tw-text-slate-500 tw-pl-7 tw-mt-1 blur-text">
            {getProtectedValue(companyDetails?.category?.name)}
          </div>
        </div>

        {/* Industry */}
        <div className="tw-flex tw-flex-col">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Building2 size={16} className="tw-text-slate-500" />
            <span className="tw-text-xs tw-font-bold tw-text-slate-700">Industry</span>
          </div>
          <div className="tw-text-xs tw-font-medium tw-text-slate-500 tw-pl-7 tw-mt-1">
            {companyDetails?.industry?.name || "-"}
          </div>
        </div>

        {/* Technographics */}
        <div className="tw-flex tw-flex-col">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Cpu size={16} className="tw-text-slate-500" />
            <span className="tw-text-xs tw-font-bold tw-text-slate-700">Technographics</span>
          </div>
          <div className="tw-text-xs tw-font-medium tw-text-slate-500 tw-pl-7 tw-mt-1 blur-text">
            {getProtectedValue(companyDetails?.technographics)}
          </div>
        </div>

        {/* Updated On */}
        <div className="tw-flex tw-flex-col">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Calendar size={16} className="tw-text-slate-500" />
            <span className="tw-text-xs tw-font-bold tw-text-slate-700">Updated On</span>
          </div>
          <div className="tw-text-xs tw-font-medium tw-text-slate-500 tw-pl-7 tw-mt-1 blur-text">
            {lastUpdatedText}
          </div>
        </div>
      </div>

      {/* Product and Services Box */}
      <div className="tw-flex tw-flex-col tw-gap-2 tw-pt-2">
        <div className="tw-flex tw-items-center tw-gap-2">
          <ShoppingBag size={16} className="tw-text-slate-500" />
          <span className="tw-text-xs tw-font-bold tw-text-slate-700">Product and Services</span>
        </div>
        <div className="tw-text-xs tw-font-medium tw-text-slate-500 tw-pl-7 tw-leading-relaxed blur-text">
          {productServicesText ? (
            <TruncateTest
              description={productServicesText}
              onAuth={() => (isAuthenticated ? checkLoginStatus?.() : setShowEmailPhoneModal(true))}
            />
          ) : (
            <span className="tw-text-slate-400">-</span>
          )}
        </div>
      </div>

      {/* Unlock Contact Details Banner when unauthenticated */}
      {!isAuthenticated && (
        <div className="tw-mt-8 tw-p-4 tw-rounded-2xl tw-bg-gradient-to-r tw-from-indigo-50/70 tw-via-purple-50/40 tw-to-pink-50/30 tw-border tw-border-solid tw-border-indigo-100 tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-4">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-white tw-shadow-sm tw-border tw-border-solid tw-border-indigo-100 tw-flex tw-items-center tw-justify-center tw-text-[#5D44FF] tw-flex-shrink-0">
              <Mail size={18} />
            </div>
            <div>
              <h4 className="tw-text-xs tw-font-bold tw-text-slate-800 tw-m-0">
                Unlock Direct Email and Phone Numbers
              </h4>
              <p className="tw-text-[11px] tw-text-slate-500 tw-m-0 tw-mt-0.5">
                Get direct access to key executive contacts and verified company intelligence.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowEmailPhoneModal(true)}
            className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-h-10 tw-bg-transparent hover:tw-bg-indigo-50/50 tw-text-[#5D44FF] hover:tw-text-indigo-600 tw-font-bold tw-text-sm tw-rounded-xl tw-border tw-border-solid tw-border-[#5D44FF] hover:tw-border-indigo-600 tw-transition-all hover:tw-scale-[1.02] tw-outline-none tw-cursor-pointer tw-whitespace-nowrap tw-flex-shrink-0"
          >
            <Mail size={15} className="tw-text-[#5D44FF]" />
            <span>View Email and Phone</span>
          </button>
        </div>
      )}

      {/* View Email and Phone Modal */}
      <ViewEmailPhoneModal
        open={showEmailPhoneModal}
        onClose={() => setShowEmailPhoneModal(false)}
      />
    </div>
  );
};

export default AboutCompany;
